"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFormattedDateTime = exports.formatDistanceToNow = exports.defaultDateDiffNames = exports.getTimeMaskFromUserPreferences = exports.getTimeFormat = exports.getStartOfDistanceForDate = exports.getDateTime = exports.removeTimeFromDateValue = exports.daysDiffFns = exports.daysDiff = exports.getToday = exports.isDateValid = exports.textToDateValue = exports.fnsFormatTime = exports.languageToFnsLocale = exports.getDateFormat = exports.getFormattedDate = exports.formatDateValueToDisplayFormat = exports.replaceDate = exports.addMinutesSafe = exports.addDaysSafe = exports.parseDefaultValue = exports.isDateValue = exports.isDateValuesEqual = exports.getNow = exports.formatIsoWithTzOffset = exports.getUtcOffset = exports.changeTimezone = exports.dateToDateValue = exports.addDurationToDateValue = exports.dateValueToDate = exports.dateValueToString = exports.emptyDateValue = exports.cutDateTimezone = exports.dateToMoment = exports.toMomentDate = exports.parseTime = exports.parseDate = void 0;
const date_fns_1 = require("date-fns");
const locale_1 = require("date-fns/locale");
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const date_fns_tz_1 = require("date-fns-tz");
const fast_memoize_1 = __importDefault(require("fast-memoize"));
const lodash_es_1 = require("lodash-es");
const isString_1 = __importDefault(require("lodash-es/isString"));
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const types_1 = require("@smartsuite/types");
const object_1 = require("./object");
const texts_1 = require("../texts");
const intl_1 = require("../locale/intl");
const timezones_1 = require("../constants/timezones");
const maxDateValue = 8640000000000000;
const fustyYear = 1900; // timezones and localisations works incorrectly before
/**
 * Maps user preference `language` values to `date-fns` locales.
 */
const fnsLanguageMapping = {
    en: locale_1.enUS,
    bg: locale_1.bg,
    fr: locale_1.fr,
    de: locale_1.de,
    it: locale_1.it,
    nl: locale_1.nl,
    pl: locale_1.pl,
    pt: locale_1.ptBR,
    ro: locale_1.ro,
    ru: locale_1.ru,
    es: locale_1.es,
    sv: locale_1.sv,
    uk: locale_1.uk,
    hi: locale_1.hi,
    "zh-hans": locale_1.zhCN,
    ja: locale_1.ja,
    // cs (Czech) language is not supported by our system everywhere
    // and added to map in calendar-view
    cs: locale_1.cs,
};
const CURRENT_YEAR_DATE_FORMATS_MAPPING = {
    "D M YYYY": {
        standard: "d MMM",
        compact: "d M yyyy",
        long: "EEE, d MMM",
        full: "EEEE, d MMMM",
    },
    "D-M-YYYY": {
        standard: "d MMM",
        compact: "d-M-yyyy",
        long: "EEE, d MMM",
        full: "EEEE, d MMMM",
    },
    "DD-MM-YYYY": {
        standard: "d MMM",
        compact: "dd-MM-yyyy",
        long: "EEE, d MMM",
        full: "EEEE, d MMMM",
    },
    "DD.MM.YYYY": {
        standard: "d MMM",
        compact: "dd.MM.yyyy",
        long: "EEE, d MMM",
        full: "EEEE, d MMMM",
    },
    "DD/MM/YYYY": {
        standard: "d MMM",
        compact: "d/M/yyyy",
        long: "EEE, d MMM",
        full: "EEEE, d MMMM",
    },
    "MM.DD.YYYY": {
        standard: "MMM d",
        compact: "MM.dd.yyyy",
        long: "EEE, MMM d",
        full: "EEEE, MMMM d",
    },
    "MM/DD/YYYY": {
        standard: "MMM d",
        compact: "M/d/yyyy",
        long: "EEE, MMM d",
        full: "EEEE, MMMM d",
    },
    "YYYY-MM-DD": {
        standard: "MMM d",
        compact: "yyyy-MM-dd",
        long: "EEE, MMM d",
        full: "EEEE, MMMM d",
    },
    "YYYY/MM/DD": {
        standard: "MMM d",
        compact: "yyyy/M/d",
        long: "EEE, MMM d",
        full: "EEEE, MMMM d",
    },
};
const DATE_FORMATS_MAPPING = {
    "D M YYYY": {
        standard: "d MMM, yyyy",
        compact: "d M yyyy",
        long: "EEE, d MMM, yyyy",
        full: "EEEE, d MMMM, yyyy",
    },
    "D-M-YYYY": {
        standard: "d MMM, yyyy",
        compact: "dd-MM-yyyy",
        long: "EEE, d MMM, yyyy",
        full: "EEEE, d MMMM, yyyy",
    },
    "DD-MM-YYYY": {
        standard: "d MMM, yyyy",
        compact: "dd-MM-yyyy",
        long: "EEE, d MMM, yyyy",
        full: "EEEE, d MMMM, yyyy",
    },
    "DD.MM.YYYY": {
        standard: "d MMM, yyyy",
        compact: "dd.MM.yyyy",
        long: "EEE, d MMM, yyyy",
        full: "EEEE, d MMMM, yyyy",
    },
    "DD/MM/YYYY": {
        standard: "d MMM, yyyy",
        compact: "d/M/yyyy",
        long: "EEE, d MMM, yyyy",
        full: "EEEE, d MMMM, yyyy",
    },
    "MM.DD.YYYY": {
        standard: "MMM d, yyyy",
        compact: "MM.dd.yyyy",
        long: "EEE, MMM d, yyyy",
        full: "EEEE, MMMM d, yyyy",
    },
    "MM/DD/YYYY": {
        standard: "MMM d, yyyy",
        compact: "M/d/yyyy",
        long: "EEE, MMM dd, yyyy",
        full: "EEEE, MMMM dd, yyyy",
    },
    "YYYY-MM-DD": {
        standard: "yyyy, MMM d",
        compact: "yyyy-MM-dd",
        long: "EEE, yyyy, MMM d",
        full: "EEEE, yyyy, MMMM d",
    },
    "YYYY/MM/DD": {
        standard: "yyyy, MMM d",
        compact: "yyyy/M/d",
        long: "EEE, yyyy, MMM d",
        full: "EEEE, yyyy, MMMM d",
    },
};
const DATE_PASTE_YEAR_RANGE = {
    yearFrom: 1000,
    yearTo: new Date().getUTCFullYear() + 999,
};
/**
 * Reference date for parsing the textual value.
 * Year value is important since it determines how "yy" values are parsed.
 * Year is used for parsing the century of yy on the following basis:
 * If default value is 2000, then
 * - text year from 00 to 49 -> 2000 to 2049
 * - text year from 50 to 99 -> 1950 to 1999
 * We can move the +-50 year range by adjusting the default year.
 */
const REFERENCE_DATE = new Date("06 Jun 2000,00:00:00").getTime();
/**
 * Parses the given string and return an instance of Date in specified timezone.
 */
function parseDate(value, timeZone = "UTC") {
    if (!value) {
        return null;
    }
    const date = (0, date_fns_1.parseISO)(value);
    return changeTimezone(date, timeZone);
}
exports.parseDate = parseDate;
function parseTime(time) {
    if (!time) {
        return null;
    }
    const timeFormats = timePasteFormats(time);
    for (const format of timeFormats) {
        const parsedTime = (0, date_fns_1.parse)(time, format, new Date());
        if ((0, date_fns_1.isValid)(parsedTime)) {
            return parsedTime;
        }
    }
    return null;
}
exports.parseTime = parseTime;
function toMomentDate(date, locale, timeZone, useTimezone = true) {
    const momentDate = moment_timezone_1.default.utc(date);
    const result = useTimezone
        ? moment_timezone_1.default.tz(date, timeZone)
        : moment_timezone_1.default.tz(timeZone).set({
            year: momentDate.year(),
            month: momentDate.month(),
            date: momentDate.date(),
            hours: 0,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
        });
    return result.locale(locale);
}
exports.toMomentDate = toMomentDate;
function dateToMoment(fieldValue, userPreferences) {
    var _a, _b;
    let dateValue = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.date;
    if (!dateValue) {
        return null;
    }
    dateValue = (fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.include_time) ? dateValue : cutDateTimezone(dateValue);
    return toMomentDate(dateValue, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale) === null || _a === void 0 ? void 0 : _a.value, (_b = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone) === null || _b === void 0 ? void 0 : _b.value, true);
}
exports.dateToMoment = dateToMoment;
function cutDateTimezone(date) {
    return date === null || date === void 0 ? void 0 : date.slice(0, 19);
}
exports.cutDateTimezone = cutDateTimezone;
function emptyDateValue() {
    return { date: null, include_time: false };
}
exports.emptyDateValue = emptyDateValue;
function dateValueToString(value, cutTime = false) {
    const dateValue = value ? value.date : value;
    if ((0, isString_1.default)(dateValue) && dateValue) {
        return cutTime ? dateValue.split("T")[0] : dateValue;
    }
    return null;
}
exports.dateValueToString = dateValueToString;
/*
 * Converts DateValue to Date
 */
function dateValueToDate(dateValue, timeZone = "UTC") {
    if (!(dateValue === null || dateValue === void 0 ? void 0 : dateValue.date)) {
        return null;
    }
    const date = (0, date_fns_1.parseISO)(dateValue.date);
    if (date.getFullYear() <= fustyYear) {
        return (0, date_fns_1.parseISO)(dateValue.date.replace("Z", ""));
    }
    return changeTimezone(date, dateValue.include_time ? timeZone : "UTC");
}
exports.dateValueToDate = dateValueToDate;
function addDurationToDateValue(value, duration, userPreferences, includeTime) {
    if (value === null || value === void 0 ? void 0 : value.date) {
        const oldValue = toMomentDate(value.date, userPreferences.locale.value, userPreferences.timezone.value, value.include_time);
        if (!value.include_time) {
            oldValue.tz("UTC", true);
        }
        // convert from user timezone to UTC
        if ((0, lodash_es_1.isBoolean)(includeTime) && !includeTime) {
            oldValue.tz("UTC", true).startOf("day");
        }
        // convert from UTC to timezone
        if ((0, lodash_es_1.isBoolean)(includeTime) && includeTime) {
            oldValue.tz(userPreferences.timezone.value, true);
        }
        return {
            date: oldValue.add(duration).toISOString(),
            include_time: (0, lodash_es_1.isBoolean)(includeTime) ? includeTime : value.include_time,
        };
    }
    return emptyDateValue();
}
exports.addDurationToDateValue = addDurationToDateValue;
/*
 * Converts Date to DateValue
 */
function dateToDateValue(date, includeTime = false, timeZone = "UTC") {
    if (includeTime) {
        date = changeTimezone(date, "UTC", timeZone);
    }
    else {
        date = (0, date_fns_1.startOfDay)(date);
    }
    const value = formatIsoWithTzOffset(date);
    return { date: value, include_time: includeTime };
}
exports.dateToDateValue = dateToDateValue;
function changeTimezone(date, targetTimezone, sourceTimezone) {
    const newDate = new Date(date);
    const sourceUTCOffset = sourceTimezone
        ? (0, exports.getUtcOffset)(sourceTimezone, newDate)
        : newDate.getTimezoneOffset();
    const targetUTCOffset = (0, exports.getUtcOffset)(targetTimezone, newDate);
    newDate.setMinutes(newDate.getMinutes() + sourceUTCOffset - targetUTCOffset);
    // If the target timezone has a different offset due to daylight saving time,
    // we need to correct the time.
    const dstOffset = targetUTCOffset - (0, exports.getUtcOffset)(targetTimezone, newDate);
    if (dstOffset !== 0) {
        newDate.setMinutes(newDate.getMinutes() + dstOffset);
    }
    return newDate;
}
exports.changeTimezone = changeTimezone;
/**
 * Returns the offset in minutes between the time zone and UTC time.
 */
exports.getUtcOffset = (0, fast_memoize_1.default)((timezone, date) => {
    return ((0, date_fns_tz_1.getTimezoneOffset)(timezone, date) / 60000) * -1;
});
function formatIsoWithTzOffset(date, tzOffset = ".000Z") {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    const hours = `${date.getHours()}`.padStart(2, "0");
    const minutes = `${date.getMinutes()}`.padStart(2, "0");
    const seconds = `${date.getSeconds()}`.padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${tzOffset}`;
}
exports.formatIsoWithTzOffset = formatIsoWithTzOffset;
/**
 * Returns the current date and time, taking into account the user's timezone.
 *
 * This is necessary in the case when the user machine has time, for example, 13-00 with the GMT+2
 * time zone, and the time zone GMT+14 is specified in the UserPreferences (the next day has begun).
 * As a result, we get the wrong current date for calculations, for example, using the
 * differenceInDays function.
 */
const getNow = (timezone) => {
    if (!timezone) {
        return new Date();
    }
    return changeTimezone(new Date(), timezone);
};
exports.getNow = getNow;
function isDateValuesEqual(dateValue1, dateValue2, timeZone) {
    if (!isDateValue(dateValue1) || !isDateValue(dateValue2)) {
        return (0, react_fast_compare_1.default)(dateValue1, dateValue2);
    }
    const date1 = dateValueToDate(dateValue1, timeZone);
    const date2 = dateValueToDate(dateValue2, timeZone);
    return ((date1 === null || date1 === void 0 ? void 0 : date1.getTime()) === (date2 === null || date2 === void 0 ? void 0 : date2.getTime()) &&
        dateValue1.include_time === dateValue2.include_time);
}
exports.isDateValuesEqual = isDateValuesEqual;
function isDateValue(value) {
    if (!(0, object_1.isObject)(value)) {
        return false;
    }
    const hasAllProperties = "date" in value && "include_time" in value;
    if (!hasAllProperties) {
        return false;
    }
    if (!value.date) {
        return true;
    }
    // min valid date has format YYYY-mm-dd
    return typeof value.date === "string" && value.date.length >= 10;
}
exports.isDateValue = isDateValue;
function parseDefaultValue(defaultValue, timeZone) {
    if (!defaultValue) {
        return defaultValue;
    }
    const isTodayOrTodayPlus = defaultValue.startsWith("now");
    if (isTodayOrTodayPlus) {
        let date = new Date();
        date = changeTimezone(date, timeZone);
        date = (0, date_fns_1.startOfDay)(date);
        const days = parseInt(defaultValue.split("+")[1], 10);
        if (days) {
            date = addDaysSafe(date, days);
        }
        return formatIsoWithTzOffset(date);
    }
    return `${defaultValue}T00:00:00.000Z`;
}
exports.parseDefaultValue = parseDefaultValue;
function addDaysSafe(date, days) {
    const dateValue = date.getTime() + (0, date_fns_1.hoursToMilliseconds)(days * 24);
    if (dateValue > maxDateValue) {
        return new Date(maxDateValue);
    }
    return new Date(dateValue);
}
exports.addDaysSafe = addDaysSafe;
function addMinutesSafe(date, minutes) {
    const dateValue = date.getTime() + (0, date_fns_1.hoursToMilliseconds)(minutes / 60);
    if (dateValue > maxDateValue) {
        return new Date(maxDateValue);
    }
    return new Date(dateValue);
}
exports.addMinutesSafe = addMinutesSafe;
/*
 * Replaces year, month and day in provided DateValue
 */
function replaceDate(dateValue, timezone, year, month, dayOfMonth) {
    if (!(dateValue === null || dateValue === void 0 ? void 0 : dateValue.date)) {
        return dateToDateValue(new Date(year, month, dayOfMonth), dateValue === null || dateValue === void 0 ? void 0 : dateValue.include_time, timezone);
    }
    let value;
    // @ts-expect-error Need to refactor logic
    value = dateValueToDate(dateValue, timezone);
    value = (0, date_fns_1.set)(value, { year, month, date: dayOfMonth });
    return dateToDateValue(value, dateValue === null || dateValue === void 0 ? void 0 : dateValue.include_time, timezone);
}
exports.replaceDate = replaceDate;
function formatDateValueToDisplayFormat(dateValue, userPreferences, displayFormat, timezone, displayTimezone, dateFormat, timeFormat, keepYear, dateTimeSeparator = "at") {
    if (!(dateValue === null || dateValue === void 0 ? void 0 : dateValue.date)) {
        return "";
    }
    const tz = timezone !== null && timezone !== void 0 ? timezone : userPreferences.timezone.value;
    const date = dateValueToDate(dateValue, tz);
    if (!date) {
        return "";
    }
    const now = (0, exports.getNow)(tz);
    const isCurrentYear = (0, date_fns_1.isSameYear)(now, date);
    let formattedDate = getFormattedDate(date, userPreferences, displayFormat, isCurrentYear && !keepYear, dateFormat);
    if (dateValue.include_time) {
        const formattedTime = fnsFormatTime(date, userPreferences, timeFormat);
        formattedDate =
            dateTimeSeparator === "at"
                ? (0, intl_1.$t)(texts_1.dateText.date_at_time, { formattedDate, formattedTime })
                : `${formattedDate}${dateTimeSeparator}${formattedTime}`;
        if (displayTimezone) {
            const tzInfo = timezones_1.timezones.find((t) => t.value === tz);
            formattedDate = tzInfo
                ? `${formattedDate} ${tzInfo.short_name}`
                : formattedDate;
        }
    }
    return formattedDate;
}
exports.formatDateValueToDisplayFormat = formatDateValueToDisplayFormat;
/**
 * Formats a given date according to user preferences.
 * We use locale, language and timezone from user preferences for formatting.
 */
function getFormattedDate(value, userPreferences, displayFormat, forCurrentYearDate, dateFormat) {
    const fnsLanguage = (0, exports.languageToFnsLocale)(userPreferences.language);
    const format = getDateFormat(dateFormat !== null && dateFormat !== void 0 ? dateFormat : userPreferences.locale.date_format, displayFormat, forCurrentYearDate);
    return (0, date_fns_1.format)(value, format, {
        locale: fnsLanguage,
    });
}
exports.getFormattedDate = getFormattedDate;
function getDateFormat(format, displayFormat, forCurrentYearDate) {
    const mapping = forCurrentYearDate
        ? CURRENT_YEAR_DATE_FORMATS_MAPPING
        : DATE_FORMATS_MAPPING;
    // Fallback to "MM/DD/YYYY" if the format is not in the mapping
    const formatMapping = mapping[format] || mapping["MM/DD/YYYY"];
    // Fallback to "standard" if the displayFormat is not in the mapping[format]
    return formatMapping[displayFormat] || formatMapping.standard;
}
exports.getDateFormat = getDateFormat;
const languageToFnsLocale = (language) => {
    return fnsLanguageMapping[language.value];
};
exports.languageToFnsLocale = languageToFnsLocale;
function fnsFormatTime(date, userPreferences, timeFormat) {
    const format = timeFormat !== null && timeFormat !== void 0 ? timeFormat : userPreferences.locale.time_format;
    const fnsLanguage = (0, exports.languageToFnsLocale)(userPreferences.language);
    return (0, date_fns_1.format)(date, types_1.TIME_FORMATS_MAPPING[format], { locale: fnsLanguage });
}
exports.fnsFormatTime = fnsFormatTime;
/**
 * Parse text as DateValue
 *
 * @param text              pasting text
 * @param userPreferences   preferences of current user
 */
const textToDateValue = (text, userPreferences) => {
    // Drop all values that shorter than 3 symbols
    if (!text || (text === null || text === void 0 ? void 0 : text.length) < 3) {
        return emptyDateValue();
    }
    let tz = userPreferences.timezone.value;
    for (const timezone of timezones_1.timezones) {
        if (text.endsWith(timezone.short_name)) {
            tz = timezone.value;
            text = text.slice(0, -timezone.short_name.length).trim();
            break;
        }
    }
    if (isISODate(text)) {
        const isoDate = (0, date_fns_1.parseISO)(text);
        if ((0, exports.isDateValid)(isoDate)) {
            return dateToDateValue(isoDate, hasTime(isoDate), tz);
        }
    }
    const dateFormat = userPreferences.locale.date_format;
    const fnsLanguage = (0, exports.languageToFnsLocale)(userPreferences.language);
    const dateFormats = dateTimePasteFormats(dateFormat, text);
    for (const format of dateFormats) {
        const refDate = getRefDate(format);
        const parsedDate = (0, date_fns_1.parse)(text, format, refDate, {
            locale: fnsLanguage,
        });
        if ((0, exports.isDateValid)(parsedDate)) {
            return dateToDateValue(parsedDate, hasTime(parsedDate), tz);
        }
    }
    return emptyDateValue();
};
exports.textToDateValue = textToDateValue;
const isISODate = (text) => {
    // ISO8601 Date in our formats need to be longer than 6 symbols to prevent some false behaviour
    return (text === null || text === void 0 ? void 0 : text.length) >= 6 && text.includes("-");
};
/**
 * Get a reference date to parse the text against.
 */
const getRefDate = (format) => {
    // When partial year is entered, we use a fixed ref date.
    // When to year is entered, when we use current date.
    return format.includes("y") ? REFERENCE_DATE : Date.now();
};
const isDateValid = (value) => {
    const { yearFrom, yearTo } = DATE_PASTE_YEAR_RANGE;
    return ((0, date_fns_1.isValid)(value) && (0, date_fns_1.getYear)(value) >= yearFrom && (0, date_fns_1.getYear)(value) <= yearTo);
};
exports.isDateValid = isDateValid;
/**
 * Create list of datetime formats that can be used for parsing pasteString
 *
 *
 * @param dateFormat    basic format used for getting first token month or day
 * @param pasteString   string that will be parsed
 */
const dateTimePasteFormats = (dateFormat = "MM/DD/YYYY", pasteString = "") => {
    const dateFormats = datePasteFormats(dateFormat, pasteString);
    const dateSeparator = dateTimeSeparator(pasteString);
    const timeFormats = timePasteFormats(pasteString);
    // Join each date format, each datetime separator and each time format and remove duplicates
    return dateFormats
        .concat(dateFormats.flatMap((format) => timeFormats.map((timeFormat) => `${format}${dateSeparator}${timeFormat}`)))
        .filter(function (item, index, array) {
        return array.indexOf(item) === index;
    });
};
const hasTime = (value) => {
    return !!(value.getHours() || value.getMinutes());
};
const getToday = () => {
    const date = `${(0, date_fns_1.formatISO)(new Date(), {
        representation: "date",
    })}T00:00:00.000Z`;
    return {
        date,
        include_time: false,
    };
};
exports.getToday = getToday;
/**
 * Create list of date formats that can be used for parsing pasteString
 *
 * It combines all formats that can be and remove formats that cannot used for this paste string.
 * This removing provide a lot of speedup for parsing process.
 *
 * @param dateFormat    basic format used for getting first token month or day
 * @param pasteString   string that will be parsed
 */
const datePasteFormats = (dateFormat = "MM/DD/YYYY", pasteString = "") => {
    // Remove formats that unused for this pasteString
    const delimiters = ["/", " - ", "-", ".", ",", " ", ""].filter((delimiter) => pasteString.includes(delimiter));
    let firstToken = "d";
    let secondToken = "M";
    if (dateFormat.startsWith("M")) {
        firstToken = "M";
        secondToken = "d";
    }
    // Create basic list of formats depends on dateFormat parameter
    const dateFormats = [
        `${firstToken}`,
        `${firstToken}/${secondToken}`,
        `${secondToken}`,
        `${secondToken}/${firstToken}`,
        `${firstToken}/${secondToken}/yy`,
        `${secondToken}/${firstToken}/yy`,
        `yy/${firstToken}/${secondToken}`,
        `yy/${secondToken}/${firstToken}`,
        `${firstToken}/${secondToken}/yyyy`,
        `${secondToken}/${firstToken}/yyyy`,
        `yyyy/${firstToken}/${secondToken}`,
        `yyyy/${secondToken}/${firstToken}`,
    ];
    // Combine all formats and all separators
    const dateFormatParts = dateFormats.map((format) => format.split("/"));
    const formats = delimiters.flatMap((delimiter) => dateFormatParts.map((format) => format.join(delimiter)));
    // Add formats that can be used for long month parsing. Fex: April 12
    const customMonthFormats = [
        "MMMM do",
        "do MMMM",
        "EEEE MMMM do",
        "EEEE, MMMM do",
        // Duplicate yy and yyyyy formats because of 2020 year parsing
        // If we parse 20 with yyyyy format we will get 0020 year but we want 2020
        "MMMM do yy",
        "MMMM do, yy",
        "do MMMM yy",
        "do MMMM, yy",
        "EEEE, MMMM do, yy",
        "EEEE MMMM do, yy",
        "EEEE, MMMM do yy",
        "EEEE MMMM do yy",
        "yy MMMM do",
        "MMMM do yyyyy",
        "MMMM do, yyyyy",
        "do MMMM yyyyy",
        "do MMMM, yyyyy",
        "EEEE, MMMM do, yyyyy",
        "EEEE MMMM do, yyyyy",
        "EEEE, MMMM do yyyyy",
        "EEEE MMMM do yyyyy",
        "yyyy MMMM do",
        "yyyy, MMM d",
        "yyyy, MMM dd",
        "yyyy, MMMM d",
        "yyyy, MMMM dd",
    ];
    return [...customMonthFormats, ...formats];
};
/**
 * Get separator that can be used for split date and time for this pasteString
 *
 * Result escaped by quotes because of token escaping in date-fns
 *
 * @param pasteString   string that will be parsed
 */
const dateTimeSeparator = (pasteString = "") => {
    const delimiter = [" at ", " at", "at ", "at", " "].find((item) => pasteString.includes(item));
    return `'${delimiter !== null && delimiter !== void 0 ? delimiter : " "}'`;
};
/**
 * Create list of time paste formats that can be used for this pasteString
 *
 * @param pasteString   string that will be parsed
 */
const timePasteFormats = (pasteString = "") => {
    const formats = [
        "H",
        "k/m",
        "H/m",
        "ha",
        "ah",
        "h a",
        "a h",
        "h/ma",
        "ah/m",
        "h/m a",
        "a h/m",
        "HH:mm:ss",
    ];
    // Don't change this order. It needs to start from ':'
    // because this format can be joined with date formats
    const delimiters = [":", ",", ".", "-", " ", ""].filter((item) => pasteString.includes(item));
    return formats.flatMap((format) => delimiters.map((delimiter) => format.split("/").join(delimiter)));
};
function daysDiff(one, two, includeTime) {
    const dateOne = (0, moment_timezone_1.default)(one);
    const dateTwo = (0, moment_timezone_1.default)(two);
    if (includeTime) {
        return dateOne.diff(dateTwo, "days");
    }
    return (0, moment_timezone_1.default)(dateOne).startOf("day").diff(dateTwo.startOf("day"), "days");
}
exports.daysDiff = daysDiff;
function daysDiffFns(one, two, includeTime = false) {
    if (includeTime) {
        return (0, date_fns_1.differenceInDays)(one, two);
    }
    const startToday = (0, date_fns_1.startOfDay)(one);
    const startOfDate = (0, date_fns_1.startOfDay)(two);
    return (0, date_fns_1.differenceInDays)(startToday, startOfDate);
}
exports.daysDiffFns = daysDiffFns;
function removeTimeFromDateValue(dateValue, timeZone) {
    if (!dateValue.include_time) {
        return dateValue;
    }
    const date = dateValueToDate(dateValue, timeZone);
    if (!date) {
        return dateValue;
    }
    return dateToDateValue(date, false, timeZone);
}
exports.removeTimeFromDateValue = removeTimeFromDateValue;
/**
 * Converts a date ISO string to a date time object, taking into account the user's time zone.
 */
const getDateTime = (dateISOString, userPreferences) => {
    const date = new Date(dateISOString);
    return changeTimezone(date, userPreferences.timezone.value);
};
exports.getDateTime = getDateTime;
const daysDiffDaysAgoThreshold = 29;
const getStartOfDistanceForDate = (date, userPreferences) => {
    const now = (0, exports.getNow)(userPreferences.timezone.value);
    const startToday = (0, date_fns_1.startOfDay)(now);
    const startOfDate = (0, date_fns_1.startOfDay)(date);
    const dateDiff = (0, date_fns_1.differenceInDays)(startToday, startOfDate);
    if (dateDiff <= daysDiffDaysAgoThreshold) {
        return startOfDate;
    }
    const firstOfMonth = (0, date_fns_1.startOfMonth)(date);
    const monthsDiff = (0, date_fns_1.differenceInMonths)(startToday, firstOfMonth);
    if (monthsDiff < 12) {
        return firstOfMonth;
    }
    return (0, date_fns_1.startOfYear)(date);
};
exports.getStartOfDistanceForDate = getStartOfDistanceForDate;
function getTimeFormat(timeFormat) {
    return timeFormat === "24h" ? "HH:mm" : "h:mm A";
}
exports.getTimeFormat = getTimeFormat;
const getTimeMaskFromUserPreferences = (userPreferences) => (userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.locale.time_format) === "12h" ? "h:mm a" : "HH:mm";
exports.getTimeMaskFromUserPreferences = getTimeMaskFromUserPreferences;
exports.defaultDateDiffNames = {
    0: (0, intl_1.$t)(texts_1.defaultDateDiffNamesText.today),
    1: (0, intl_1.$t)(texts_1.defaultDateDiffNamesText.yesterday),
};
/**
 * Returns the distance between the date and the current system date as a human readable string.
 *
 * To customize the output of the result, you can redefine the text of the distance using
 * dateDiffNames arg.
 */
const formatDistanceToNow = (dateISOString, userPreferences, dateDiffNames = exports.defaultDateDiffNames) => {
    if (!dateISOString) {
        return "";
    }
    const date = (0, exports.getDateTime)(dateISOString, userPreferences);
    const now = (0, exports.getNow)(userPreferences.timezone.value);
    const startToday = (0, date_fns_1.startOfDay)(now);
    const startOfDate = (0, date_fns_1.startOfDay)(date);
    const dateDiff = (0, date_fns_1.differenceInDays)(startToday, startOfDate);
    const hasDateDiffName = dateDiff in dateDiffNames && dateDiffNames[dateDiff];
    if (hasDateDiffName) {
        return dateDiffNames[dateDiff];
    }
    const secondsDiff = (0, date_fns_1.differenceInSeconds)(now, date);
    if (secondsDiff >= 0 && secondsDiff < 60) {
        return (0, intl_1.$t)(texts_1.dateFormatDistanceText.secondsAgo);
    }
    if (dateDiff > 6 && dateDiff < 30) {
        // formatDistanceStrict does not have 'week' unit
        // (unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year');
        // thus we need to count week diff
        const week = (0, date_fns_1.differenceInWeeks)(startToday, startOfDate);
        return (0, intl_1.$t)(texts_1.dateFormatDistanceText.weekAgo, { itemCount: week });
    }
    if (dateDiff === 30 && (0, date_fns_1.isSameMonth)(now, date)) {
        // formatDistanceStrict function incorectly formats distance between first and last day of a month
        // if a month has 31 days(e.g. formatDistanceStrict("2021-10-31", "2021-10-01") returns 29 days ago)
        return (0, intl_1.$t)(texts_1.dateFormatDistanceText.daysAgo);
    }
    const isToday = dateDiff === 0;
    const fnsLanguage = (0, exports.languageToFnsLocale)(userPreferences.language);
    const toDate = isToday
        ? date
        : (0, exports.getStartOfDistanceForDate)(date, userPreferences);
    const fromDate = dateDiff <= daysDiffDaysAgoThreshold ? now : (0, date_fns_1.startOfMonth)(now);
    return (0, date_fns_1.formatDistanceStrict)(toDate, fromDate, {
        addSuffix: true,
        locale: fnsLanguage,
        roundingMethod: "floor",
    });
};
exports.formatDistanceToNow = formatDistanceToNow;
const getFormattedDateTime = (inputDate, userPreferences, options = {
    displayFormat: "standard",
    includeCurrentYear: false,
    dateAndTimeSeparator: " • ",
}) => {
    const timezone = userPreferences.timezone.value;
    const now = (0, exports.getNow)(timezone);
    let dateToFormat;
    if (typeof inputDate === "string") {
        dateToFormat = (0, exports.getDateTime)(inputDate, userPreferences);
    }
    else {
        // inputDate is a Date object, apply timezone conversion
        dateToFormat = changeTimezone(inputDate, timezone);
    }
    const isCurrentYear = (0, date_fns_1.isSameYear)(now, dateToFormat);
    const formattedDate = getFormattedDate(dateToFormat, userPreferences, options.displayFormat, isCurrentYear && !options.includeCurrentYear);
    const formattedTime = fnsFormatTime(dateToFormat, userPreferences);
    return `${formattedDate}${options.dateAndTimeSeparator}${formattedTime}`;
};
exports.getFormattedDateTime = getFormattedDateTime;
